export function cleanObject<T extends Record<string, unknown>>(obj: T): T {
  return Object.entries(obj)
    .filter(([_, v]) => v !== null && v !== undefined && v !== "")
    .reduce((acc, [k, v]) => {
      // Recursively clean only plain objects (not Date, RegExp, etc.)
      const isPlainObject =
        typeof v === "object" &&
        v !== null &&
        !Array.isArray(v) &&
        Object.getPrototypeOf(v) === Object.prototype;
      if (isPlainObject) {
        const cleanedNested = cleanObject(v as Record<string, unknown>);
        // Only add if the nested object has properties after cleaning
        if (Object.keys(cleanedNested).length > 0) {
          acc[k as keyof T] = cleanedNested as T[keyof T];
        }
      } else {
        acc[k as keyof T] = v as T[keyof T];
      }
      return acc;
    }, {} as T);
}

type Pagination<T> = {
  data: T[];
  total: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export function createPagination<T>(
  data: T[],
  pageSize: number,
  currentPage: number,
): Pagination<T> {
  const paginatedData = data.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  return {
    data: paginatedData,
    total: data.length,
    pageSize,
    currentPage: 1,
    totalPages: Math.ceil(data.length / pageSize),
    hasNextPage: currentPage < Math.ceil(data.length / pageSize),
    hasPreviousPage: currentPage > 1,
  };
}

export function areAllValuesFalsy<T extends Record<string, unknown>>(
  obj: T,
): boolean {
  return Object.values(obj).every((value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === "object" && value !== null) {
      return areAllValuesFalsy(value as Record<string, unknown>);
    }
    if (Number.isNaN(value)) {
      return true;
    }
    return !value;
  });
}
